import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Search, AlertCircle } from 'lucide-react';
import { userService } from '@/services/user.service';
import { useToast } from '@/hooks/use-toast';

interface CompletarCadastroProps {
  isOpen: boolean;
  userData: any;
  onSuccess: (newData: any) => void;
}

const CompletarCadastro: React.FC<CompletarCadastroProps> = ({
  isOpen,
  userData,
  onSuccess
}) => {
  const { toast } = useToast();

  // Estados do formulário
  const [chave_pix, setChavePix] = useState('');
  const [tipoChave, setTipoChave] = useState('');
  const [maxLengthChavePix, setMaxLengthChavePix] = useState(0);
  const [placeHolderChavePix, setPlaceHolderChavePix] = useState('Digite a chave');

  const [cep, setCep] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [cidade, setCidade] = useState('');
  const [uf, setUf] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');

  const [btnDisabled, setBtnDisabled] = useState(false);
  const [searchingCep, setSearchingCep] = useState(false);
  const [cepError, setCepError] = useState('');

  const changeTipoChave = (tipoSelected: string) => {
    setTipoChave(tipoSelected);
    setChavePix('');
    setMaxLengthChavePix(0);
    setPlaceHolderChavePix('Digite a chave');

    if (tipoSelected === 'cpf_cnpj') {
      setMaxLengthChavePix(18);
      setPlaceHolderChavePix('Digite o CPF/CNPJ');
    } else if (tipoSelected === 'email') {
      setMaxLengthChavePix(1000);
      setPlaceHolderChavePix('Digite o e-mail');
    } else if (tipoSelected === 'phone') {
      setMaxLengthChavePix(15);
      setPlaceHolderChavePix('Digite o telefone');
    } else if (tipoSelected === 'random') {
      setMaxLengthChavePix(1000);
      setPlaceHolderChavePix('Digite a chave aleatória');
    }
  };

  const formataCep = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const aplicarMascaraChavePix = (value: string) => {
    if (tipoChave === 'cpf_cnpj') {
      const cleaned = value.replace(/\D/g, '');

      if (cleaned.length <= 11) {
        // Aplicar a máscara de CPF
        return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      } else {
        // Aplicar a máscara de CNPJ
        return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      }
    }

    if (tipoChave === 'phone') {
      const cleaned = value.replace(/\D/g, '');
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }

    return value;
  };

  const buscarCep = async () => {
    if (!cep || cep.length < 8) {
      setCepError('CEP deve ter 8 dígitos');
      return;
    }

    setSearchingCep(true);
    setCepError('');

    try {
      const resp = await userService.searchCep(cep);

      if (resp && resp.erro === "true") {
        setCepError('CEP não encontrado, verifique e tente novamente');
        return;
      }

      setUf(resp.uf);
      setCidade(resp.localidade);
      setLogradouro(`${resp.bairro}, ${resp.logradouro}`);
    } catch (error) {
      setCepError('Erro ao buscar CEP');
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setSearchingCep(false);
    }
  };

  const handleSubmit = async () => {
    setBtnDisabled(true);

    if (!userData?.id) {
      toast({
        title: "Erro",
        description: "Dados do usuário não encontrados",
        variant: "destructive",
      });
      setBtnDisabled(false);
      return;
    }

    const data = {
      id: userData.id,
      pix: chave_pix || userData.cpf,
      zip_code: cep,
      line_1: logradouro,
      city: cidade,
      state: uf,
      number: numero,
      complement: complemento
    };

    try {
      const resp = await userService.atualizarCadastroIncompleto(data);

      if (resp && resp.message) {
        const newData = { ...userData, address: data };
        localStorage.setItem("userlogged", JSON.stringify(newData));

        toast({
          title: "Sucesso",
          description: "Cadastro atualizado com sucesso",
        });

        onSuccess(newData);
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (error) {
      console.error('Erro ao atualizar cadastro:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar cadastro, tente novamente ou contate o suporte",
        variant: "destructive",
      });
    } finally {
      setBtnDisabled(false);
    }
  };

  const isFormValid = () => {
    if (userData?.role === 'cliente') {
      return (
        cep &&
        cidade &&
        uf &&
        logradouro &&
        numero
      );
    }
    return (
      tipoChave &&
      chave_pix &&
      cep &&
      cidade &&
      uf &&
      logradouro &&
      numero
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={() => { }} modal>
        <DialogContent
          className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Completar Cadastro</DialogTitle>
            <DialogDescription>
              Antes de prosseguir, por favor complete seu cadastro:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Chave PIX */}
            {userData?.role !== 'cliente' && (
              <div className="space-y-4">
                <Label className="text-sm font-semibold">
                  Sua chave PIX <span className="text-red-500">*</span>
                </Label>

                <Select value={tipoChave} onValueChange={changeTipoChave}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo de chave*" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cpf_cnpj">CPF/CNPJ</SelectItem>
                    <SelectItem value="phone">Telefone</SelectItem>
                    <SelectItem value="email">E-mail</SelectItem>
                    <SelectItem value="random">Chave Aleatória</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  disabled={!tipoChave}
                  value={chave_pix}
                  onChange={(e) => {
                    const maskedValue = aplicarMascaraChavePix(e.target.value);
                    setChavePix(maskedValue);
                  }}
                  maxLength={maxLengthChavePix}
                  placeholder={placeHolderChavePix + '*'}
                />
              </div>
            )}


            {/* CEP */}
            <div className="space-y-4">
              <Label className="text-sm font-semibold">
                CEP <span className="text-red-500">*</span>
              </Label>

              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    value={cep}
                    onChange={(e) => {
                      const formatted = formataCep(e.target.value);
                      setCep(formatted);
                      setCepError('');
                    }}
                    maxLength={9}
                    placeholder="12345-678"
                  />
                </div>
                <Button
                  onClick={buscarCep}
                  disabled={searchingCep || cep.length < 8}
                  className="bg-primary hover:bg-primary-hover"
                >
                  {searchingCep ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Buscar
                </Button>
              </div>

              {cepError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{cepError}</AlertDescription>
                </Alert>
              )}
            </div>

            {/* Endereço (só aparece após buscar CEP com sucesso) */}
            {cidade && uf && (
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div>
                    <p className="text-sm">
                      <strong>Cidade/UF:</strong> {cidade} - {uf}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">
                      Endereço <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={logradouro}
                      onChange={(e) => setLogradouro(e.target.value)}
                      placeholder="Logradouro"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">
                      Número <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={numero}
                      onChange={(e) => setNumero(e.target.value)}
                      placeholder="Número"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Complemento</Label>
                    <Input
                      value={complemento}
                      onChange={(e) => setComplemento(e.target.value)}
                      placeholder="Complemento/referência"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Botão de submit */}
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid() || btnDisabled}
              className="w-full bg-primary hover:bg-primary-hover"
            >
              {btnDisabled ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Enviando, aguarde...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CompletarCadastro;